import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import dayjs from 'dayjs';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  Resources,
  Task,
  TimeEntry,
} from '@totem/components/ticketing/ticketing';
import { TimeEntryCreateExtras } from '@totem/components/ticketing/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
type Props = {
  resources: Resources;
  tasks: Task[];
};
const TimeEntryDialog = ({ resources, tasks }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { ticket, onAction, action, actionExtras } = useContext(TicketContext);
  const [isSending, setIsSending] = useState(false);
  const [allowTaskSelection, setAllowTaskSelection] = useState<boolean>(true);
  const userResoruce = isNotNull(resources)
    ? resources.find((chk) => chk.email === userProfile.email)
    : null;
  const userResourceId = isNotNull(userResoruce) ? userResoruce?.id : '';
  const [timeEntryData, setTimeEntryData] = useState<TimeEntry>({
    id: '',
    taskId: '',
    resourceId: userResourceId,
    date: dayjs().toISOString(),
    hoursLogged: 0.0,
    billable: !!(
      isNotNull(ticket) &&
      isNotNull(ticket.ticket.project) &&
      ticket.ticket.project.billingType === 'Time & Material'
    ),
    description: '',
    billingId: '',
    createdByType: 'USER',
    createdByName: userProfile.email,
    createdById: userProfile.id,
    createdAt: dayjs().toISOString(),
    isDeleted: false,
  });
  const ticketId =
    isNotNull(ticket) && isNotNull(ticket.ticket) ? ticket.ticket.id : '';
  useEffect(() => {
    if (isNotNull(actionExtras)) {
      try {
        const extras: TimeEntryCreateExtras = JSON.parse(actionExtras);
        if (
          extras.objectReference !== null &&
          isNotNull(timeEntryData) &&
          timeEntryData.taskId === ''
        ) {
          if (extras.objectReference.name === 'Task') {
            setTimeEntryData((prevState) => ({
              ...prevState,
              taskId: extras.objectReference.id,
            }));

            setAllowTaskSelection(false);
          }
        }
      } catch {
        /* empty */
      }
    }
  }, [actionExtras]);
  const [form] = Form.useForm();
  useEffect(() => {
    if (timeEntryData.resourceId === '') {
      const defaultResources = resources.filter(
        (chk) => chk.email === userProfile.email,
      );
      if (isNotNull(defaultResources) && defaultResources.length > 0) {
        setTimeEntryData((prevState) => ({
          ...prevState,
          resourceId: defaultResources[0].id,
        }));
      }
    }
  }, [resources]);
  const sendTimeEntry = useCallback(
    async (paramTicketId: string, payload: TimeEntry) => {
      if (!isSending) {
        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/timeTracking`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            form.resetFields();

            onAction('refresh', '');
          }
        });
      }
    }, // End async
    [],
  );
  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        setTimeEntryData((prevState) => ({
          ...prevState,
          resourceId: values.resourceId,
          date: values.date.toDate(),
          hoursLogged: values.hoursLogged,
          description: values.description,
          billable: values.billable,
        }));
      })
      .then(() => {
        sendTimeEntry(ticketId, timeEntryData);
      })
      .catch((info) => {
        logger.error('Validate Failed:', info);
      });
  };
  return (
    <Modal
      open={action === 'show-time-entry-create'}
      title="Add Time Entry"
      onCancel={() => {
        form.resetFields();

        onAction('', '');
      }}
      onOk={handleOk}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();

            onAction('', '');
          }}
        >
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk}>
          Submit
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          resourceId: timeEntryData.resourceId,
          taskId: timeEntryData.taskId,
          date: dayjs(timeEntryData.date),
        }}
      >
        <Form.Item
          label="Resource"
          name="resourceId"
          rules={[{ required: true, message: 'Please select a resource' }]}
        >
          <Select
            placeholder="Select resource"
            value={timeEntryData.resourceId}
            onChange={(value) =>
              setTimeEntryData((prevState) => ({
                ...prevState,
                resourceId: value,
              }))
            }
          >
            {resources.map((res) => (
              <Select.Option key={res.id} value={res.id}>
                {res.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {allowTaskSelection && (
          <Form.Item label="Task" name="taskId">
            <Select
              placeholder="Select task"
              value={timeEntryData.taskId}
              onChange={(value) =>
                setTimeEntryData((prevState) => ({
                  ...prevState,
                  taskId: value,
                }))
              }
            >
              {tasks.map((tsk) => (
                <Select.Option key={tsk.id} value={tsk.id}>
                  {tsk.number}
                  {': '}
                  {tsk.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          label="Date"
          name="date"
          rules={[{ required: true, message: 'Please select date' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            onChange={(newValue) =>
              setTimeEntryData((prevState) => ({
                ...prevState,
                date: newValue.toISOString(),
              }))
            }
          />
        </Form.Item>
        <Row gutter={24}>
          <Col span={16}>
            <Form.Item
              label="Hours Worked"
              name="hoursLogged"
              rules={[{ required: true, message: 'Please enter hours logged' }]}
            >
              <InputNumber
                min={0}
                max={24}
                step={0.25}
                style={{ width: '100%' }}
                value={timeEntryData.hoursLogged}
                onChange={(value) =>
                  setTimeEntryData((prevState) => ({
                    ...prevState,
                    hoursLogged: value,
                  }))
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Billable" name="billable" valuePropName="checked">
              <Switch
                value={timeEntryData.billable}
                onChange={(checked: boolean) =>
                  setTimeEntryData((prevState) => ({
                    ...prevState,
                    billable: checked,
                  }))
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Please enter description' }]}
        >
          <Input.TextArea
            rows={3}
            value={timeEntryData.description}
            onChange={(event) =>
              setTimeEntryData((prevState) => ({
                ...prevState,
                description: event.target.value,
              }))
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
export default TimeEntryDialog;
